import type { Dispatch, MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  blobToBase64,
  cancelRun,
  type DocTypeConfig,
  fetchRunDocument,
  getRunSnapshot,
  listActiveRuns,
  type RunSnapshotDTO,
  pauseRun,
  resolveLatestRunIdForDoc,
  resumeRun,
} from "../../api/client";
import {
	clearDocument,
	getActiveRunId,
	isSyncReady,
	loadWordHydrationMarker,
	saveWordHydrationMarker,
} from "../../services/wordWriter";
import { checkDocEmpty, checkDocHasContent } from "../../services/wordProbe";
import { shouldLiveProject } from "../liveProjection";
import { runPostSectionCitationPipeline } from "../services/citationPipeline";
import { loadRunIntoLiveDoc } from "../services/loadRunIntoLiveDoc";
import type { LoomFreshSelection } from "../components/LoomFresh";
import type { PlanData } from "../components/LoomPlanReview";
import type { LoomSelectionCommit } from "../components/LoomSelection";
import type { ChatMessage } from "../messageProjection";
import { getCachedDocId, saveDocumentSession, scheduleSave } from "../services/documentSessionService";
import {
  RUN_FAILED_AFTER_DRAFTING_ERROR,
  deriveRunSummaryFromSpine,
  mapCustomSpineDTO,
  mapSpineDTO,
  reconcileSpineWithRunStatus,
  restoresToIdle,
  restoresToPostRun,
  withReadyTimes,
} from "../services/runHydration";
import type { RunIdentity } from "../sseAdapter";
import type { VariantCEvent } from "../state";
import { toPersistedSpineSnapshot } from "../utils/spineSnapshot";
import { computeLockedModuleIds, type SpineSection } from "./useVariantCRun";

interface BrowserWordHydrationMarker {
  runId: string;
  snapshotVersion: string | null;
  snapshotTs: number | null;
}

const BROWSER_WORD_HYDRATION_MARKER_PREFIX = "asthra_word_hydration_marker_fallback";

function _browserWordHydrationMarkerKey(docId: string | null): string {
  return `${BROWSER_WORD_HYDRATION_MARKER_PREFIX}:${docId || "unknown"}`;
}

function _readBrowserWordHydrationMarker(docId: string | null): BrowserWordHydrationMarker | null {
  try {
    const raw = localStorage.getItem(_browserWordHydrationMarkerKey(docId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BrowserWordHydrationMarker>;
    if (typeof parsed.runId !== "string") return null;
    if (parsed.snapshotVersion !== null && parsed.snapshotVersion !== undefined && typeof parsed.snapshotVersion !== "string") {
      return null;
    }
    if (parsed.snapshotTs !== null && parsed.snapshotTs !== undefined && typeof parsed.snapshotTs !== "number") {
      return null;
    }
    return {
      runId: parsed.runId,
      snapshotVersion: parsed.snapshotVersion ?? null,
      snapshotTs: parsed.snapshotTs ?? null,
    };
  } catch {
    return null;
  }
}

function _writeBrowserWordHydrationMarker(docId: string | null, marker: BrowserWordHydrationMarker): void {
  try {
    localStorage.setItem(_browserWordHydrationMarkerKey(docId), JSON.stringify(marker));
  } catch {
    // Ignore browser storage failures in restricted contexts.
  }
}

export function shouldHydrateWord(args: {
  runId: string;
  snapshotVersion: string | null | undefined;
  snapshotTs?: number | null;
  lastHydratedRunId: string | null;
  lastHydratedVersion: string | null;
  lastHydratedTs?: number | null;
}): boolean {
  const { runId, snapshotVersion, snapshotTs, lastHydratedRunId, lastHydratedVersion, lastHydratedTs } = args;
  if (!lastHydratedRunId) return true;
  if (runId !== lastHydratedRunId) return true;

  if (snapshotVersion && lastHydratedVersion) {
    if (snapshotVersion !== lastHydratedVersion) return true;
    return false;
  }

  if (typeof snapshotTs === "number" && typeof lastHydratedTs === "number") {
    if (snapshotTs !== lastHydratedTs) return true;
    return false;
  }

  return true;
}

/** Shape stored in pendingHydration while awaiting spine seeding. */
export interface PendingHydration {
  snapshot: RunSnapshotDTO;
  status: RunSnapshotDTO;
  /** When true, dispatch RUN_COMPLETED after hydration. */
  autoComplete?: true;
  /** When true, reset to idle after hydration (stopped run). */
  resetToIdle?: true;
}

export interface UseRunControlParams {
		/** From state reducer */
		runId: string | null;
		runState: string;
		dispatch: Dispatch<VariantCEvent>;
		// Error + subscription signalling
		setStartError: (e: string | null) => void;
		/** Surfaces a persistent, dismissable warning when a Stop request
		 *  could not be confirmed against the backend (network/5xx after
		 *  retries). Unlike ``setStartError`` this renders at the shell root
		 *  so it survives the post-run / idle transition a stop triggers.
		 *  Optional so existing callers/tests need not supply it. */
		setStopWarning?: (w: string | null) => void;
		setRunSubscriptionStartMode: (m: "history" | "latest") => void;
		setRunSubscriptionNonce: (fn: (v: number) => number) => void;
		setRestoreInFlight: (v: boolean) => void;
		/** Optional setter for the shell's restore-hydration progress %.
		 *  Set to 0..100 while replaying sections into Word; set to null
		 *  when restore finishes or aborts. */
		setRestoreHydrationProgressPct?: (v: number | null) => void;
		/** Setter for the shell-owned ``terminalRunStatus`` flag. Flipped to
		 *  true inside the run-restore effect when the latest run's status is
		 *  ``completed`` / ``failed`` / ``cancelled``; reset on each new
		 *  restore attempt. ``useVariantCRun`` reads the matching state to
		 *  skip its SSE subscribe effect (Redis stream is gone for terminal
		 *  runs and a subscribe would 404). */
		setTerminalRunStatus: (v: boolean) => void;
		// Reset helpers (shell state + refs)
		setRunStartedAt: (t: number | null) => void;
		setFocusedSectionId: (id: string | null) => void;
		scaffoldedRef: MutableRefObject<boolean>;
		insertionModeRef: MutableRefObject<"section" | "batch">;
		/** Whether this run projects sections into the live doc as they land.
		 *  Re-established on restore so a mid-run pane reload keeps projecting. */
		liveProjectionRef: MutableRefObject<boolean>;
		/** Chain a Word-write closure onto the single-writer queue owned by
		 *  useWordWriteQueue. Used here to serialize the bootstrap rehydrate
		 *  (loadRunIntoLiveDoc) with any SSE writes that arrive afterward —
		 *  guarantees the rehydrate finishes before forward-painting touches
		 *  Word, so Office.js never has two consumers contending. */
		enqueueWordWrite: <T>(fn: () => Promise<T>) => Promise<T>;
		resetDraftFlowState: () => void;
		/** The loaded run's persisted submission region (snapshot ``region``),
		 *  handed to the shell so its selection carries it after a reload. */
		onRunRegion?: (region: string) => void;
		// handleReset only
		setActiveSelection: (s: LoomFreshSelection | null) => void;
		setDocConfig: (c: DocTypeConfig | null) => void;
		setSectionCommit: (c: LoomSelectionCommit | null) => void;
		setPlanData: (p: PlanData | null) => void;
		setPlanId: (id: string | null) => void;
		setPersistedChatMessages: (m: ChatMessage[]) => void;
		/** Clears the in-memory restored spine snapshot so the next bootstrap
		 *  can't re-hydrate a stale spine after a reset/stop. Mirrors the wipe
		 *  done by the rotate-project path (handleConfirmRotateProject). */
		setPersistedSpineSnapshot: (s: SpineSection[] | null) => void;
		userIdentifier: string | null | undefined;
		// Hydration effect deps (from useVariantCRun)
		sections: SpineSection[];
		hydrateSections: (sections: SpineSection[]) => void;
		/** From useVariantCRun — for identity persist effect. */
		identity: RunIdentity | null;
		// Run-restore effect deps (from useSessionBootstrap)
		sessionLoading: boolean;
		bootstrapRunId: string | null;
		bootstrapDismissedRunId?: string | null;
		restoredInsertionMode?: "batch" | "section" | null;
		docConfig: DocTypeConfig | null;
	}

export interface UseRunControlResult {
  handlePauseRun: () => Promise<void>;
  handleResumePausedRun: () => Promise<void>;
  /** Cancels the run and lands on the post-run confirmation banner (spine
   *  view), keeping the run attached even when nothing was drafted. Always
   *  resolves ``true``. */
  handleStopRun: () => Promise<boolean>;
  /** Re-run run→Word projection for the current run on demand. */
  handleResyncHydration: () => Promise<void>;
  /**
   * Sync the auto-restore dedupe ref with a run id that was just loaded via
   * a manual path (e.g. the Drafts panel Load button). Without this, the
   * auto-restore effect can re-fire after ``handleRunLoaded`` hydrates
   * sections and trigger a second ``loadRunIntoLiveDoc`` round, which
   * re-opens the empty-doc window and risks the rotate-project modal.
   */
  markRunRestored: (runId: string) => void;
}

/** Backoff (ms) before each re-try of the restore's run snapshot fetch. */
export const RESTORE_SNAPSHOT_RETRY_DELAYS_MS = [500, 1500, 3000] as const;

/** Most recent live (pending/running/paused) run for ``docId``, or null.
 *  Errors fall through to null so the restore keeps its stamped-id path. */
export async function findLiveRunIdForDoc(docId: string): Promise<string | null> {
  try {
    const resp = await listActiveRuns({ docId, limit: 10 });
    const runs = (resp?.runs ?? []).filter((r) => !r.doc_id || r.doc_id === docId);
    if (runs.length === 0) return null;
    const ts = (r: (typeof runs)[number]) => r.started_at ?? r.created_at ?? "";
    return [...runs].sort((a, b) => ts(b).localeCompare(ts(a)))[0].run_id;
  } catch (err) {
    console.warn("[useRunControl] run-restore: listActiveRuns failed (non-fatal):", err);
    return null;
  }
}

/** ``getRunSnapshot`` with backoff retries — one transient 5xx/network blip
 *  right after a reload must not drop the restore (shell would stay idle and
 *  Pause/Kill never render). Returns null after the last failure or once
 *  ``isCancelled`` reports the invocation was superseded. */
async function getRunSnapshotWithRetry(runId: string, isCancelled: () => boolean): Promise<RunSnapshotDTO | null> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await getRunSnapshot(runId);
    } catch (err) {
      const delay = RESTORE_SNAPSHOT_RETRY_DELAYS_MS[attempt];
      console.warn(`[useRunControl] run-restore: getRunSnapshot failed (attempt ${attempt + 1}):`, err);
      if (delay === undefined || isCancelled()) return null;
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (isCancelled()) return null;
    }
  }
}

export function useRunControl({
		runId,
		runState,
		dispatch,
		setStartError,
		setStopWarning,
		setRunSubscriptionStartMode,
		setRunSubscriptionNonce,
		setRestoreInFlight,
		setRestoreHydrationProgressPct,
		setTerminalRunStatus,
		setRunStartedAt,
		setFocusedSectionId,
		scaffoldedRef,
		insertionModeRef,
		liveProjectionRef,
		enqueueWordWrite,
		resetDraftFlowState,
		onRunRegion,
		setActiveSelection,
		setDocConfig,
		setSectionCommit,
		setPlanData,
		setPlanId,
		setPersistedChatMessages,
		setPersistedSpineSnapshot,
		userIdentifier,
		sections,
		hydrateSections,
		identity,
		sessionLoading,
		bootstrapRunId,
		bootstrapDismissedRunId = null,
		restoredInsertionMode = null,
		docConfig,
	}: UseRunControlParams): UseRunControlResult {
		const [pendingHydration, setPendingHydration] =
			useState<PendingHydration | null>(null);

		// One-shot guard for run-restore: prevents re-firing if state.runId
		// is briefly cleared (RESET_TO_IDLE) or docConfig is swapped.
		const restoredRunRef = useRef<string | null>(null);

		// Guard against concurrent resume attempts (e.g. rapid double-clicks or
		// re-entry while a prior attempt is awaiting the 409 resync). Without
		// this each click fires an independent resumeRun() + dispatch sequence,
		// which can leave the reducer in an inconsistent mid-transition state.
		const resumeInFlightRef = useRef(false);

		// Live mirror of runState so a delayed pause-reconcile can tell whether the
		// run has already settled (SSE ``RUN_PAUSED`` arrived) before it acts.
		const runStateRef = useRef(runState);
		runStateRef.current = runState;
		// Pending pause-ack reconcile timer — cleared once we leave "pausing".
		const pauseReconcileTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
			null,
		);

		// Ref for identity persist — avoid spamming scheduleSave on re-renders.
		const persistedIdentityRef = useRef<{
			trial: string;
			session: string;
		} | null>(null);

		// -- Run-restore effect (bootstrap rehydration of a drafted doc) ------
		// On reopen of a document we resolve the LATEST run for this doc id
		// (RunBundleDoc index sorted by completed_at, falling back to the
		// bootstrap's active run_id for in-progress runs that don't have a
		// bundle yet) and prime pendingHydration. The hydration effect below
		// then restores the spine AND re-projects the run's drafted content
		// into the live Word body.
		useEffect(() => {
			if (sessionLoading) return;
			if (!docConfig) return;
			if (sections.length === 0) return;
			const docIdForResolve = getCachedDocId();
			if (!docIdForResolve && !bootstrapRunId) return;
			let cancelled = false;
			// This invocation's claim on the restoredRunRef dedupe, and whether it
			// got as far as dispatching RUN_RESTORED. After a login reload the deps
			// settle in several renders; a superseded invocation that claimed the
			// ref but never dispatched must hand the claim back, or the newer
			// invocation hits the dedupe guard and nobody restores the run — the
			// shell stays idle and Pause/Kill never render.
			let claimedRunId: string | null = null;
			let committed = false;
			setRestoreInFlight(false);
			setRestoreHydrationProgressPct?.(null);
			// No terminal-flag reset here: this effect re-runs whenever docConfig
			// or sections.length changes — which a Drafts / Home Load does right
			// after flipping the flag on — and most re-runs return at the dedupe
			// guard. Clearing it un-gated the SSE hook on a finished run, which
			// subscribed in "history" mode and pinned "Preparing run · Starting
			// the drafting engine…" over the post-run spine. A real restore sets
			// the flag from the snapshot below, before RUN_RESTORED.
			void (async () => {
				const [wordHydrationMarker, activeRunId] = await Promise.all([
					loadWordHydrationMarker(),
					getActiveRunId(),
				]);
				// A live (pending/running/paused) run for this doc beats whatever
				// run id is stamped into the Word doc: after a refresh or
				// logout→login mid-run the stamp/marker can still name the PREVIOUS
				// run, and restoring that one shows a finished run with no
				// Pause/Kill while the new run keeps drafting server-side.
				const liveRunId = docIdForResolve
					? await findLiveRunIdForDoc(docIdForResolve)
					: null;
				if (cancelled) return;
				const preferredWordRunId =
					liveRunId ?? wordHydrationMarker?.runId ?? activeRunId ?? null;
				if (
					liveRunId &&
					(wordHydrationMarker?.runId ?? activeRunId ?? liveRunId) !== liveRunId
				) {
					console.info(
						`[spine-refresh-debug] restore_live_run_preferred liveRunId=${liveRunId} markerRunId=${wordHydrationMarker?.runId ?? "none"} activeRunId=${activeRunId ?? "none"}`,
					);
				}
				let runIdToRestore: string | null = preferredWordRunId;
				if (!runIdToRestore) {
					runIdToRestore = docIdForResolve
						? await resolveLatestRunIdForDoc(docIdForResolve, bootstrapRunId)
						: (bootstrapRunId ?? null);
				}
				if (cancelled) return;
				if (!runIdToRestore) return;
				if (bootstrapDismissedRunId && runIdToRestore === bootstrapDismissedRunId) {
					console.info(
						`[spine-refresh-debug] restore_skipped reason=dismissed_match runId=${runIdToRestore} dismissedRunId=${bootstrapDismissedRunId}`,
					);
					return;
				}
				const emptyCheck = await checkDocEmpty();
				if (cancelled) return;
				const hasWordRunMetadata = Boolean(
					wordHydrationMarker?.runId || activeRunId || liveRunId,
				);
				const hasBootstrapRunContext = Boolean(bootstrapRunId);
				if (
					emptyCheck.isEmpty === true &&
					!hasWordRunMetadata &&
					!hasBootstrapRunContext
				) {
					console.info(
						`[spine-refresh-debug] restore_skipped reason=blank_document runId=${runIdToRestore} markerRunId=${wordHydrationMarker?.runId ?? "none"} activeRunId=${activeRunId ?? "none"} bootstrapRunId=${bootstrapRunId ?? "none"}`,
					);
					return;
				}
				if (emptyCheck.isEmpty === true) {
					console.info(
						`[spine-refresh-debug] restore_blank_guard_bypassed runId=${runIdToRestore} markerRunId=${wordHydrationMarker?.runId ?? "none"} activeRunId=${activeRunId ?? "none"} bootstrapRunId=${bootstrapRunId ?? "none"}`,
					);
				}
				if (restoredRunRef.current === runIdToRestore) return;
				restoredRunRef.current = runIdToRestore;
				claimedRunId = runIdToRestore;
				insertionModeRef.current = restoredInsertionMode ?? "batch";
				setRestoreInFlight(true);
				const snapshot = await getRunSnapshotWithRetry(
					runIdToRestore,
					() => cancelled,
				);
				if (cancelled) return;
				if (!snapshot) {
					restoredRunRef.current = null;
					setRestoreInFlight(false);
					return;
				}
				const status = snapshot;
				const isCompleted = status.status === "completed";
				const isCancelled = status.status === "cancelled";
				const isFailed = status.status === "failed";
				const isTerminated = isCancelled || isFailed;
				// A cancelled OR failed run that produced at least one drafted
				// section restores to the post-run confirmation surface (like a
				// completed run) so the writer sees — and can load — the work
				// finished before the kill/failure. The backend still retains
				// those drafts; reconcileSpineWithRunStatus repaints them from
				// completed_module_ids in the hydration effect below. Restoring
				// a drafted-but-failed run to idle instead wipes runId and
				// leaves the spine visible yet inert (Inspector is gated on
				// state.runId). A terminated run with no drafts resets to idle
				// as before.
				const restoreToPostRun = restoresToPostRun(status);
				const restoreToIdle = restoresToIdle(status);
				console.info(
					`[spine-refresh-debug] restore_snapshot runId=${status.run_id} status=${status.status} sourceHint=${status.source_hint ?? "unknown"} currentModule=${status.current_module ?? "none"} completedModules=${status.modules_completed}/${status.modules_total} restoreToPostRun=${restoreToPostRun} restoreToIdle=${restoreToIdle}`,
				);
				// Flip the terminal flag BEFORE the RUN_RESTORED dispatch so the
				// SSE hook sees it on the very next render and skips its subscribe
				// effect — avoids a 404 on the evicted Redis stream.
				setTerminalRunStatus(isCompleted || isTerminated);
				setRunSubscriptionStartMode("latest");
				console.info(
					`[spine-refresh-debug] restore_seed_subscription_mode runId=${status.run_id} mode=latest terminal=${isCompleted || isTerminated}`,
				);
				// NOTE: ``running`` / ``paused`` runs no longer bail out here. The
				// partial spine + partial drafted content is restored so the user
				// sees what's already produced. Re-attaching SSE for further
				// section_ready events is out of scope for this change.
				committed = true;
				dispatch({ type: "RUN_RESTORED", runId: runIdToRestore });
				// A restored run — completed (post-run) or still live (mid-run /
				// paused) — belongs on the spine. The scene chain checks
				// viewMode==="drafts" BEFORE the run-state→spine branch, so without
				// this an open-from-Drafts restore stays on Home even though the run
				// restored underneath (the "bounce to Drafts" bug). Mirrors
				// handleRunLoaded / handleStopRun / useSessionBootstrap, which force
				// spine. A terminated-with-no-drafts run (restoreToIdle) stays on Home.
				if (!restoreToIdle) {
					dispatch({ type: "VIEW_MODE_SET", mode: "spine" });
				}
				setPendingHydration({
					snapshot,
					status,
					...(restoreToPostRun ? { autoComplete: true as const } : {}),
					...(restoreToIdle ? { resetToIdle: true as const } : {}),
				});
			})();
			return () => {
				cancelled = true;
				if (
					claimedRunId &&
					!committed &&
					restoredRunRef.current === claimedRunId
				) {
					console.info(
						`[spine-refresh-debug] restore_cancelled_release runId=${claimedRunId}`,
					);
					restoredRunRef.current = null;
				}
				setRestoreInFlight(false);
				setRestoreHydrationProgressPct?.(null);
			};
		}, [
			sessionLoading,
			bootstrapRunId,
			restoredInsertionMode,
			docConfig,
			sections.length,
			dispatch,
			setRestoreInFlight,
			setRestoreHydrationProgressPct,
			setRunSubscriptionStartMode,
			setTerminalRunStatus,
			bootstrapDismissedRunId,
		]);

		// -- Resume hydration effect (Spec 18 P4) ------------------------------
		// Drains pendingHydration once the spine's sections have been seeded
		// from docConfig AND the reducer reflects the resumed runId.
		// biome-ignore lint/correctness/useExhaustiveDependencies: sections/sections.length intentionally crafted
		useEffect(() => {
			if (!pendingHydration) return;
			if (sections.length === 0) return;
			if (runId !== pendingHydration.status.run_id) return;
			const { snapshot, status, autoComplete, resetToIdle } = pendingHydration;
			setPendingHydration(null);
			if (snapshot.region) onRunRegion?.(snapshot.region);
			console.info(
				`[spine-refresh-debug] applying_spine_hydration runId=${status.run_id} renderedSections=${sections.length} completedModuleIds=${(status.completed_module_ids ?? []).join(",")} currentModule=${status.current_module ?? "none"} snapshotVersion=${snapshot.snapshot_version} snapshotSpineSections=${snapshot.spine.length} snapshotTimelineEvents=${snapshot.events.length} autoComplete=${!!autoComplete} resetToIdle=${!!resetToIdle}`,
			);

			void (async () => {
				try {
					let restoredSections: SpineSection[];
					if (snapshot.submission_mode === "custom") {
						// Custom submission: the spine IS the existing document's heading
						// outline (document order, synthetic CUSTOM-N ids, green where the
						// heading has content). Take the server rows verbatim — NEVER merge
						// onto the doctype template skeleton (mapSpineDTO), which would drop
						// every custom row (ids don't match template modules) and render the
						// full template instead. Mirrors handleRunLoaded's custom branch.
						restoredSections = mapCustomSpineDTO(snapshot);
					} else {
						const spineDTO = {
							doc_id: snapshot.doc_id,
							run_id: snapshot.run_id,
							run_status: snapshot.status,
							spine: snapshot.spine,
						};
						const snapshotSections = mapSpineDTO(spineDTO, sections);

						// The persisted UI snapshot can lag the backend by one or more
						// completed modules (it's saved opportunistically). Reconcile it
						// against the AUTHORITATIVE live run status so the spine reflects
						// real progress after a refresh: completed_module_ids → drafted,
						// current_module → drafting (only when not already completed, so
						// a finished section never shows a stale in-progress pulse). This
						// also keeps the spine's "N/M drafted" counter in sync with the
						// run status's modules_completed.
						restoredSections = reconcileSpineWithRunStatus(snapshotSections, status);
					}

					restoredSections = withReadyTimes(restoredSections, snapshot.events);
					hydrateSections(restoredSections);
					// Scope the spine to the run's own sections: the snapshot's rows
					// are the run's scoped modules (Guru 2026-10-14).
					if (snapshot.submission_mode !== "custom" && snapshot.spine.length > 0) {
						dispatch({
							type: "RUN_SCOPE_SET",
							runId: status.run_id,
							moduleIds: snapshot.spine.map((r) => r.id),
						});
					}

					// Restore paused shell state explicitly so controls show Resume
					// immediately after refresh (no SSE event required).
					if (status.status === "paused") {
						// A failed-but-resumable run arrives here as paused
						// (presentResumableFailure); say why it stopped.
						dispatch(
							status.failure_notice
								? { type: "RUN_PAUSED", notice: status.failure_notice }
								: { type: "RUN_PAUSED" },
						);
					}

					// Re-project the backend SSOT into the live Word body for EVERY
					// restored run — terminal or live. The rehydrate is chained onto
					// the single Word-write queue exposed by useWordWriteQueue, so
					// any SSE ``section_ready`` that arrives mid-rehydrate is queued
					// behind it and runs after — never two Office.js consumers
					// contending for the context (which used to freeze the UI on
					// logout/login). The prior "skip for live/paused/pending" branch
					// left the .docx body empty on re-login because SSE re-attaches
					// with ``lastEventId="latest"`` (no historical replay).
					if (
						docConfig &&
						status.run_id &&
						// Custom submission: the run was built on the writer's EXISTING
						// Word document — its body is authoritative and holds no
						// server-side drafted sections to re-project. Never rebuild it.
						snapshot.submission_mode !== "custom"
					) {
						try {
							// Re-establish live projection for the REST of this run: a
							// pane reload mid-run must keep projecting sections as they
							// land. Deliberately independent of the hydrate decision
							// below, which stays keyed on insertionMode.
							liveProjectionRef.current = shouldLiveProject(
								docConfig,
								snapshot.submission_mode,
							);
							const shouldAutoHydrateWord =
								insertionModeRef.current !== "batch";
							if (!shouldAutoHydrateWord) {
								console.info(
									"[useRunControl] skip loadRunIntoLiveDoc: batch mode requires manual load",
									{
										runId: status.run_id,
										mode: insertionModeRef.current,
									},
								);
								setRestoreHydrationProgressPct?.(null);
								scaffoldedRef.current = true;
							} else {
								const docIdForMarker = getCachedDocId();
								const lastMarker =
									(await loadWordHydrationMarker()) ??
									_readBrowserWordHydrationMarker(docIdForMarker);
								let shouldHydrate = shouldHydrateWord({
									runId: status.run_id,
									snapshotVersion: snapshot.snapshot_version,
									snapshotTs: snapshot.snapshot_ts,
									lastHydratedRunId: lastMarker?.runId || null,
									lastHydratedVersion: lastMarker?.snapshotVersion ?? null,
									lastHydratedTs: lastMarker?.snapshotTs ?? null,
								});
								// PRIMARY guard — does this document ALREADY hold this run's draft?
								// A re-login / task-pane reopen re-runs this restore, but the Word
								// DOCUMENT persists across it. ``snapshot_version`` is timestamp-
								// seeded server-side, so ``shouldHydrateWord`` above is ~always
								// true; every earlier skip-guard was defeated: the custom-prop
								// hydration marker wasn't always readable, and a FIRST-PARAGRAPH
								// emptiness probe called a 129-page doc "empty" whenever its first
								// paragraph was a blank spacer or the body hadn't rendered yet
								// (this effect fires the instant the spine seeds — it never waits
								// for Word). Each defeat = the whole body wiped and rebuilt from a
								// ~17s ``/all-sections`` fetch on login.
								//
								// Decide on DURABLE facts, in order:
								//  1. ``isSyncReady(runId)`` — the ``AsthrSyncReady`` custom prop
								//     ``markSyncReady`` writes after a successful Load. It IS the
								//     "this doc holds this run" fact, independent of render timing
								//     or layout (the sync gate already trusts it). True → skip.
								//  2. Otherwise a WHOLE-BODY text probe. Rebuild ONLY on a definite
								//     "no content". Uncertain (``null``: Office not ready / read
								//     failed) → keep the persisted body; never wipe on a guess.
								// Live runs still append new sections via SSE; the spine side-panel
								// hydrates independently either way. A genuinely empty body still
								// rebuilds — a re-login must never land blank (SSE re-attaches at
								// lastEventId="latest", no replay).
								if (shouldHydrate) {
									let docHoldsRun = false;
									try {
										docHoldsRun = (await isSyncReady(status.run_id)) === true;
									} catch {
										docHoldsRun = false;
									}
									if (docHoldsRun) {
										shouldHydrate = false;
										console.info(
											"[useRunControl] skip loadRunIntoLiveDoc: doc already holds this run (AsthrSyncReady)",
											{ runId: status.run_id, lastMarkerRunId: lastMarker?.runId ?? null },
										);
									} else {
										let hasContent: boolean | null = null;
										try {
											hasContent = (await checkDocHasContent()).hasContent;
										} catch {
											hasContent = null;
										}
										if (hasContent === false) {
											// Definitely empty → rebuild (the only case that wipes).
										} else {
											shouldHydrate = false;
											console.info(
												hasContent === true
													? "[useRunControl] skip loadRunIntoLiveDoc: body already has content"
													: "[useRunControl] skip loadRunIntoLiveDoc: body probe uncertain — keeping persisted body",
												{ runId: status.run_id, lastMarkerRunId: lastMarker?.runId ?? null },
											);
										}
									}
								}
								if (shouldHydrate) {
									setRestoreHydrationProgressPct?.(0);
									const result = await enqueueWordWrite(() =>
										loadRunIntoLiveDoc(status.run_id, docConfig, {
											onProgress: (progress) => {
												setRestoreHydrationProgressPct?.(progress.percent);
											},
										}),
									);
									console.info("[useRunControl] loadRunIntoLiveDoc complete", {
										runId: status.run_id,
										status: status.status,
										sectionsWritten: result.sectionsWritten,
									});
									// Suppress a redundant scaffold if SSE keeps streaming —
									// the doc already has the scaffold from the rehydrate.
									scaffoldedRef.current = true;
									await saveWordHydrationMarker({
										runId: status.run_id,
										snapshotVersion: snapshot.snapshot_version ?? null,
										snapshotTs: snapshot.snapshot_ts ?? null,
									});
									_writeBrowserWordHydrationMarker(docIdForMarker, {
										runId: status.run_id,
										snapshotVersion: snapshot.snapshot_version ?? null,
										snapshotTs: snapshot.snapshot_ts ?? null,
									});
								} else {
									console.info(
										"[useRunControl] skip loadRunIntoLiveDoc: unchanged snapshot",
										{
											runId: status.run_id,
											snapshotVersion: snapshot.snapshot_version,
										},
									);
									setRestoreHydrationProgressPct?.(null);
									scaffoldedRef.current = true;
								}
							}
						} catch (err) {
							console.warn("[useRunControl] loadRunIntoLiveDoc failed:", err);
							setRestoreHydrationProgressPct?.(null);
							setStartError(
								"Couldn't restore drafted content into the document — spine state is shown from cache. Try reloading the add-in.",
							);
						}
					}

					// Refresh restore should hydrate persisted chat history only.
					// Do not replay timeline events into EventLog on restore.

					// Surface any Phase 3 streaming text the backend captured during
					// the disconnect window.  ``streaming_drafts`` is keyed by
					// module_id; the values are the running draft text emitted by
					// the agent before module_complete fired.  The Word doc body
					// has already been hydrated above via ``loadRunIntoLiveDoc``
					// for fully-drafted modules — this log surfaces what the
					// currently-drafting module produced while the user was logged
					// out so we can confirm the backend kept writing during the
					// logout window.  A later iteration can inject these into the
					// inspector draft tab or the live Word body for the active
					// module; for now the durable backend persistence is the
					// load-bearing piece.
					const streaming = snapshot.streaming_drafts;
					if (streaming && Object.keys(streaming).length > 0) {
						console.info("[useRunControl] rejoin: streaming drafts received", {
							runId: status.run_id,
							modules: Object.keys(streaming),
							sizes: Object.fromEntries(
								Object.entries(streaming).map(([k, v]) => [k, v.length]),
							),
						});
					}

					if (autoComplete) {
						// Tally from the just-hydrated spine, NOT the backend
						// modules_total/modules_completed scalars: those are
						// run-scoped and drift-prone, which made the chat rail
						// say "Done. 7/7 sections drafted." while the spine
						// footer (same section list as here) showed "2/96".
						dispatch({
							type: "RUN_COMPLETED",
							summary: deriveRunSummaryFromSpine(
								restoredSections,
								status.elapsed_seconds,
							),
						});
						// A failed run restored to post-run (it drafted at least
						// one section) shouldn't read as a clean completion —
						// surface the failure without disturbing the state
						// machine (RUN_FAILED only records the error).
						if (status.status === "failed") {
							dispatch({
								type: "RUN_FAILED",
								error: RUN_FAILED_AFTER_DRAFTING_ERROR,
							});
						}
					}
					if (resetToIdle) {
						console.info(
							`[spine-refresh-debug] restore_reset_to_idle runId=${status.run_id}`,
						);
						dispatch({ type: "RESET_TO_IDLE" });
					}
				} finally {
					// After restore projection, force a fresh live-tail attach for
					// in-progress runs. This closes the refresh race where the first
					// post-restore EventSource can attach stale and remain idle until
					// a manual pause/resume bumps the subscription nonce.
					if (status.status === "pending" || status.status === "running") {
						console.info(
							`[spine-refresh-debug] restore_force_live_reattach runId=${status.run_id} status=${status.status} mode=latest nonceBump=1`,
						);
						setRunSubscriptionStartMode("latest");
						setRunSubscriptionNonce((value) => value + 1);
					}
					setRestoreInFlight(false);
				}
			})();
		}, [
			docConfig,
			enqueueWordWrite,
			hydrateSections,
			pendingHydration,
			sections,
			runId,
			dispatch,
			setRestoreInFlight,
			setRestoreHydrationProgressPct,
			setRunSubscriptionNonce,
			setRunSubscriptionStartMode,
			setStartError,
		]);

		// -- Identity persist effect -------------------------------------------
		// Persist (trial, session) the moment SSE run_metadata arrives so
		// a future reopen can rehydrate useVariantCRun.identity.
		// Ref-compare to avoid spamming scheduleSave on every re-render.
		useEffect(() => {
			if (!identity) return;
			const last = persistedIdentityRef.current;
			if (
				last &&
				last.trial === identity.trial &&
				last.session === identity.session
			) {
				return;
			}
			persistedIdentityRef.current = {
				trial: identity.trial,
				session: identity.session,
			};
			const docId = getCachedDocId();
			if (!docId) return;
			scheduleSave(docId, {
				trial: identity.trial,
				session_id: identity.session,
			});
		}, [identity]);

		// -- handlePauseRun ----------------------------------------------------
		const handlePauseRun = useCallback(async () => {
			if (!runId || runState !== "mid-run") return;
			console.info(
				`[spine-refresh-debug] pause_click runId=${runId} runState=${runState}`,
			);
			dispatch({ type: "PAUSE_REQUESTED" });
			try {
				await pauseRun(runId);
				console.info(
					`[spine-refresh-debug] pause_request_sent runId=${runId}`,
				);
				// The transition "pausing" → "paused" normally arrives as an SSE
				// ``RUN_PAUSED`` from the worker. A run whose worker is already gone
				// (a zombie restored as LIVE) never emits it, so the button would sit
				// on "Pausing…" forever. Reconcile against the authoritative snapshot
				// after a grace period IF we are still pausing — mirrors the defensive
				// snapshot fallback handleResumePausedRun already has.
				if (pauseReconcileTimerRef.current) {
					clearTimeout(pauseReconcileTimerRef.current);
				}
				pauseReconcileTimerRef.current = setTimeout(() => {
					void (async () => {
						if (runStateRef.current !== "pausing") return; // already settled
						try {
							const snap = await getRunSnapshot(runId);
							if (runStateRef.current !== "pausing") return; // settled while awaiting
							if (snap.status === "paused") {
								dispatch({ type: "RUN_PAUSED" });
								return;
							}
							// Terminal (completed / failed / cancelled) — the backend
							// reconciled a dead zombie to its true state. Move it off
							// "Pausing…" and onto the post-run (or idle) surface, mirroring
							// the restore path, instead of leaving PAUSE/KILL on a run that
							// has actually finished.
							if (
								snap.status === "completed" ||
								snap.status === "failed" ||
								snap.status === "cancelled"
							) {
								dispatch({ type: "PAUSE_REVERTED" });
								const hasDrafts = (snap.modules_completed ?? 0) > 0;
								if (snap.status === "completed" || hasDrafts) {
									dispatch({
										type: "RUN_COMPLETED",
										summary: deriveRunSummaryFromSpine(
											sections,
											snap.elapsed_seconds,
										),
									});
									if (snap.status === "failed") {
										dispatch({
											type: "RUN_FAILED",
											error: {
												code: "run_failed",
												message:
													"The run ended with an error after drafting — the drafted sections are preserved and can be opened or loaded.",
												recoverable: false,
											},
										});
									}
								} else {
									dispatch({ type: "RESET_TO_IDLE" });
								}
								console.info(
									`[spine-refresh-debug] pause_reconciled_terminal runId=${runId} status=${snap.status}`,
								);
								return;
							}
							// Still running/pending (a zombie whose heartbeat is briefly
							// fresh, or a genuinely live run mid-LLM-call) → un-stick the
							// optimistic "Pausing…" back to mid-run so the user isn't
							// trapped and can Kill.
							dispatch({ type: "PAUSE_REVERTED" });
							setStartError(
								"Couldn't pause the run — it may no longer be active. Try Kill.",
							);
							console.info(
								`[spine-refresh-debug] pause_reconciled runId=${runId} status=${snap.status}`,
							);
						} catch (reErr) {
							// A failed reconcile must NEVER leave the button stuck on
							// "Pausing…" (the original bug: this catch dispatched nothing).
							// Un-stick to mid-run so the writer can Kill instead.
							dispatch({ type: "PAUSE_REVERTED" });
							setStartError(
								"Couldn't confirm the pause — the run may no longer be active. Try Kill.",
							);
							console.warn(
								`[spine-refresh-debug] pause_reconcile_failed runId=${runId} error=${reErr instanceof Error ? reErr.message : String(reErr)}`,
							);
						}
					})();
				}, 4000);
			} catch (err) {
				const status = (err as { status?: number } | null)?.status;
				const detail = (err as { detail?: { status?: string } } | null)?.detail;
				// The backend returns 409 {code:"not_active", status:<terminal>} when
				// the run is no longer live (a zombie reconciled to its true terminal).
				// Don't show a scary "couldn't pause" error — the run is simply done;
				// settle the widget to the terminal (post-run spine, or idle) at once.
				const terminal = status === 409 ? detail?.status : undefined;
				if (terminal === "completed" || terminal === "failed" || terminal === "cancelled") {
					dispatch({ type: "PAUSE_REVERTED" });
					const hasDrafts = computeLockedModuleIds(sections).length > 0;
					if (terminal === "completed" || hasDrafts) {
						dispatch({
							type: "RUN_COMPLETED",
							summary: deriveRunSummaryFromSpine(sections),
						});
						if (terminal === "failed") {
							dispatch({
								type: "RUN_FAILED",
								error: {
									code: "run_failed",
									message:
										"The run ended with an error — the drafted sections are preserved and can be opened or loaded.",
									recoverable: false,
								},
							});
						}
					} else {
						dispatch({ type: "RESET_TO_IDLE" });
					}
					dispatch({ type: "VIEW_MODE_SET", mode: "spine" });
					console.info(
						`[spine-refresh-debug] pause_not_active_settled runId=${runId} status=${terminal}`,
					);
					return;
				}
				dispatch({ type: "PAUSE_REVERTED" });
				const message = err instanceof Error ? err.message : String(err);
				console.warn(
					`[spine-refresh-debug] pause_failed runId=${runId} message=${message}`,
				);
				setStartError(`Couldn't pause run: ${message}`);
			}
		}, [runId, runState, dispatch, setStartError, sections]);

		// Clear any pending pause-ack reconcile once we leave "pausing" (the SSE
		// ``RUN_PAUSED`` settled it, or the user navigated) so it can't fire
		// against an already-settled run.
		useEffect(() => {
			if (runState !== "pausing" && pauseReconcileTimerRef.current) {
				clearTimeout(pauseReconcileTimerRef.current);
				pauseReconcileTimerRef.current = null;
			}
		}, [runState]);

		// -- handleResumePausedRun ---------------------------------------------
		const handleResumePausedRun = useCallback(async () => {
			if (!runId) return;
			// Prevent concurrent resume attempts from stacking. Each click after the
			// first while a resume is in-flight is a no-op until the first attempt
			// fully settles (success or terminal error).
			if (resumeInFlightRef.current) {
				console.warn(
					`[spine-refresh-debug] resume_blocked_inflight runId=${runId} runState=${runState}`,
				);
				return;
			}
			resumeInFlightRef.current = true;
			console.info(
				`[spine-refresh-debug] resume_attempt_start runId=${runId} runState=${runState}`,
			);

			const attemptResume = async (): Promise<void> => {
				try {
					const resumeResp = await resumeRun(runId);
					// Success — attach to live tail. Use "latest" so SSE does NOT replay
					// historical events (including the prior paused event) that would
					// immediately flip the reducer back to paused.
					console.info(
						`[spine-refresh-debug] resume_api_success runId=${runId} response=${JSON.stringify(resumeResp)}`,
					);
					setRunSubscriptionStartMode("latest");
					dispatch({ type: "RUN_RESUMED", runId });
					console.info(
						`[spine-refresh-debug] resume_nonce_bump runId=${runId} path=primary mode=latest nonceBump=1`,
					);
					setRunSubscriptionNonce((value) => value + 1);
				} catch (err) {
					const detail = (
						err as {
							detail?: { code?: string; retry_after_seconds?: number };
						} | null
					)?.detail;
					console.warn(
						`[spine-refresh-debug] resume_api_error runId=${runId} code=${detail?.code ?? "none"} runState=${runState} error=${err instanceof Error ? err.message : String(err)}`,
					);

					if (detail?.code === "pause_pending") {
						const waitMs = Math.max(1, detail.retry_after_seconds ?? 3) * 1000;
						console.info(
							`[spine-refresh-debug] resume_pause_pending_wait runId=${runId} waitMs=${waitMs}`,
						);
						await new Promise((resolve) => setTimeout(resolve, waitMs));
						const retryResp = await resumeRun(runId);
						console.info(
							`[spine-refresh-debug] resume_retry_success runId=${runId} response=${JSON.stringify(retryResp)}`,
						);
						setRunSubscriptionStartMode("latest");
						dispatch({ type: "RUN_RESUMED", runId });
						console.info(
							`[spine-refresh-debug] resume_nonce_bump runId=${runId} path=retry mode=latest nonceBump=1`,
						);
						setRunSubscriptionNonce((value) => value + 1);
						return;
					}

					if (detail?.code === "not_paused") {
						// Backend is not paused — it may have already resumed or finished.
						// Fetch the authoritative snapshot and sync UI state to it.
						try {
							console.info(
								`[spine-refresh-debug] resume_not_paused_fetch_snapshot runId=${runId}`,
							);
							const snapshot = await getRunSnapshot(runId);
							console.info(
								`[spine-refresh-debug] resume_not_paused_snapshot runId=${runId} status=${snapshot.status} currentModule=${snapshot.current_module ?? "none"}`,
							);
							if (
								snapshot.status === "pending" ||
								snapshot.status === "running"
							) {
								// Backend is already running — attach to live tail.
								setRunSubscriptionStartMode("latest");
								dispatch({ type: "RUN_RESUMED", runId });
								console.info(
									`[spine-refresh-debug] resume_nonce_bump runId=${runId} path=not_paused_running mode=latest nonceBump=1`,
								);
								setRunSubscriptionNonce((value) => value + 1);
								return;
							}
							if (snapshot.status === "paused") {
								// Still paused — nothing to do; leave UI in paused state.
								console.info(
									`[spine-refresh-debug] resume_not_paused_backend_paused runId=${runId}`,
								);
								return;
							}
							if (restoresToPostRun(snapshot)) {
								// completed, cancelled, or failed-after-drafting: land on
								// the post-run surface like the restore path does, never
								// straight to idle (that wiped the drafted spine).
								console.info(
									`[spine-refresh-debug] resume_not_paused_post_run runId=${runId} status=${snapshot.status}`,
								);
								// Repaint the spine from the authoritative snapshot,
								// then tally the summary from that SAME section list —
								// the run-scoped modules_total/modules_completed
								// scalars drift and previously produced a "Done. N/N"
								// chat note that disagreed with the spine footer.
								const restored = reconcileSpineWithRunStatus(
									mapSpineDTO(
										{
											doc_id: snapshot.doc_id,
											run_id: snapshot.run_id,
											run_status: snapshot.status,
											spine: snapshot.spine,
										},
										sections,
									),
									snapshot,
								);
								hydrateSections(withReadyTimes(restored, snapshot.events));
								if (snapshot.spine.length > 0) {
									dispatch({ type: "RUN_SCOPE_SET", runId, moduleIds: snapshot.spine.map((r) => r.id) });
								}
								dispatch({
									type: "RUN_COMPLETED",
									summary: deriveRunSummaryFromSpine(
										restored,
										snapshot.elapsed_seconds,
									),
								});
								if (snapshot.status === "failed") {
									dispatch({
										type: "RUN_FAILED",
										error: RUN_FAILED_AFTER_DRAFTING_ERROR,
									});
								}
								return;
							}
							if (restoresToIdle(snapshot)) {
								console.info(
									`[spine-refresh-debug] resume_not_paused_terminal runId=${runId} status=${snapshot.status}`,
								);
								dispatch({ type: "RESET_TO_IDLE" });
								return;
							}
						} catch (statusErr) {
							console.warn(
								`[spine-refresh-debug] resume_not_paused_snapshot_failed runId=${runId} error=${statusErr instanceof Error ? statusErr.message : String(statusErr)}`,
							);
						}
					}

					const message = err instanceof Error ? err.message : String(err);
					setStartError(`Couldn't resume run: ${message}`);
				}
			};

			try {
				await attemptResume();
			} finally {
				resumeInFlightRef.current = false;
				console.info(
					`[spine-refresh-debug] resume_attempt_settled runId=${runId}`,
				);
			}
		}, [
			runId,
			runState,
			dispatch,
			sections,
			hydrateSections,
			setRunSubscriptionStartMode,
			setRunSubscriptionNonce,
			setStartError,
		]);

		// -- handleStopRun -----------------------------------------------------
		// Cancels the backend run and lands on the post-run surface (spine view)
		// — identical to how a refresh restores a cancelled run — so the writer
		// keeps the run attached and can load whatever was finished before the
		// kill. This holds even when NOTHING was drafted: the run stays open on
		// the spine (all rows queued) instead of resetting to idle, which on a
		// still-empty document dropped the shell onto the Home scene.
		// Always returns ``true`` (the run is kept); the boolean is retained for
		// callers that branch on it.
		const handleStopRun = useCallback(async (): Promise<boolean> => {
			const docId = getCachedDocId();

			// KILL must never hang the button on a dead/slow backend. Bound each
			// network await so a stuck request rejects (and we proceed) rather than
			// blocking forever — the local reset below runs regardless, and the
			// backend cold-cancel still flips Mongo durably.
			const withTimeout = <T,>(p: Promise<T>, ms: number): Promise<T> =>
				Promise.race([
					p,
					new Promise<never>((_, reject) =>
						setTimeout(
							() => reject(new Error(`timed out after ${ms}ms`)),
							ms,
						),
					),
				]);

			// Persist the latest spine + spine view up front so a refresh /
			// reopen can restore whatever was drafted before the kill.
			if (docId && sections.length > 0) {
				try {
					await withTimeout(
						saveDocumentSession(docId, {
							spine_snapshot: toPersistedSpineSnapshot(sections),
							view_mode: "spine",
						}),
						4000,
					);
				} catch (err) {
					console.warn(
						"[useRunControl] stop snapshot save failed/timed out (continuing):",
						err,
					);
				}
			}
			// A failed cancel is the dangerous case: the Redis cancel latch was
			// never armed, so the worker keeps drafting (and burning tokens)
			// while the UI resets to "stopped". Retry transient failures, treat
			// a 404 as success (backend reports the run is no longer active =
			// already stopped), and if it still can't be confirmed raise a
			// visible warning below rather than swallowing it. The local reset
			// proceeds either way so the writer is never wedged.
			let stopCancelUnconfirmed = false;
			if (runId) {
				const CANCEL_MAX_ATTEMPTS = 3;
				for (let attempt = 1; attempt <= CANCEL_MAX_ATTEMPTS; attempt++) {
					try {
						await withTimeout(cancelRun(runId), 6000);
						stopCancelUnconfirmed = false;
						break;
					} catch (err) {
						const status = (err as { status?: number } | null)?.status;
						if (status === 404) {
							// Run already gone — nothing left to stop.
							stopCancelUnconfirmed = false;
							break;
						}
						stopCancelUnconfirmed = true;
						console.warn(
							`[useRunControl] cancelRun attempt ${attempt}/${CANCEL_MAX_ATTEMPTS} failed:`,
							err instanceof Error ? err.message : String(err),
						);
						if (attempt < CANCEL_MAX_ATTEMPTS) {
							await new Promise((resolve) =>
								setTimeout(resolve, 400 * attempt),
							);
						}
					}
				}
			}

			// The run is terminal now — detach SSE so a late ``cancelled``
			// event can't 404 the evicted stream or flip the reducer out from
			// under the transition below.
			setTerminalRunStatus(true);
			setStartError(null);
			// Applied AFTER the setStartError(null) clear so it isn't wiped:
			// raise the stop-unconfirmed warning (or clear a stale one on a
			// confirmed stop). Renders at the shell root so it survives the
			// post-run / idle transition below.
			setStopWarning?.(
				stopCancelUnconfirmed
					? "Stop request didn't reach the server — the run may still be drafting in the background. Tap Stop again to retry."
					: null,
			);
			setFocusedSectionId(null);
			setRestoreInFlight(false);
			setRestoreHydrationProgressPct?.(null);

			// Land on the post-run confirmation banner in the spine (even with
			// zero drafted sections). Keep run_id + spine_snapshot persisted
			// (undismissed) so the live and refresh paths stay consistent —
			// the bootstrap restore resolves this same run and lands on
			// post-run too.
			scaffoldedRef.current = true;
			// Settle the section the kill interrupted: a still-"drafting"
			// module never finished, so demote it to "queued" and re-hydrate
			// so ``activeSectionId`` clears — otherwise the post-run banner
			// (and the persisted snapshot) keeps a stale pulsing dot.
			const settledSections = sections.map((s) =>
				s.status === "drafting" ? { ...s, status: "queued" as const } : s,
			);
			hydrateSections(settledSections);
			dispatch({ type: "VIEW_MODE_SET", mode: "spine" });
			// Same spine-derived tally as every other RUN_COMPLETED dispatch
			// (drafted counts only status === "drafted"; flagged reported
			// separately) so the rail message and the spine footer agree.
			dispatch({
				type: "RUN_COMPLETED",
				summary: { ...deriveRunSummaryFromSpine(settledSections), live: true },
			});
			if (docId) {
				scheduleSave(docId, {
					spine_snapshot: toPersistedSpineSnapshot(settledSections),
					view_mode: "spine",
					clear_fields: ["section_commit", "focused_section_id"],
					last_modified_by: userIdentifier ?? undefined,
				});
			}
			return true;
		}, [
			sections,
			runId,
			userIdentifier,
			setTerminalRunStatus,
			hydrateSections,
			dispatch,
			setStartError,
			setStopWarning,
			setFocusedSectionId,
			scaffoldedRef,
			setRestoreInFlight,
			setRestoreHydrationProgressPct,
		]);

		const handleResyncHydration = useCallback(async () => {
			if (!runId || !docConfig) return;
			setRestoreInFlight(true);
			setRestoreHydrationProgressPct?.(0);
			try {
				const result = await enqueueWordWrite(() =>
					loadRunIntoLiveDoc(runId, docConfig, {
						onProgress: (progress) => {
							setRestoreHydrationProgressPct?.(progress.percent);
						},
					}),
				);
				console.info("[useRunControl] manual loadRunIntoLiveDoc complete", {
					runId,
					sectionsWritten: result.sectionsWritten,
				});
				const snapshot = await getRunSnapshot(runId);
				await saveWordHydrationMarker({
					runId,
					snapshotVersion: snapshot.snapshot_version ?? null,
					snapshotTs: snapshot.snapshot_ts ?? null,
				});
				_writeBrowserWordHydrationMarker(getCachedDocId(), {
					runId,
					snapshotVersion: snapshot.snapshot_version ?? null,
					snapshotTs: snapshot.snapshot_ts ?? null,
				});
				scaffoldedRef.current = true;
			} catch (err) {
				console.warn("[useRunControl] manual loadRunIntoLiveDoc failed:", err);
				setRestoreHydrationProgressPct?.(null);
				setStartError(
					"Couldn't sync drafted content into the document. Please try again.",
				);
			} finally {
				setRestoreInFlight(false);
			}
		}, [
			runId,
			docConfig,
			enqueueWordWrite,
			setRestoreInFlight,
			setRestoreHydrationProgressPct,
			scaffoldedRef,
			setStartError,
		]);

		const markRunRestored = useCallback((runId: string) => {
			restoredRunRef.current = runId;
		}, []);

		return {
			handlePauseRun,
			handleResumePausedRun,
			handleStopRun,
			handleResyncHydration,
			markRunRestored,
		};
	}
